// src/pages/Contacts/ContactsPage.tsx
import React, { useState, useCallback } from "react";
import {
  Plus,
  Phone,
  Mail,
  Edit,
  Trash2,
  Eye,
  Clock,
  Calendar,
  User,
} from "lucide-react";
import {
  DataTable,
  PageHeader,
  LoadingSpinner,
  EmptyState,
} from "../../components/common";
import type {
  DataTableColumn,
  DataTableAction,
  DataTableBulkAction,
} from "../../components/common";
import {
  useGetContactsQuery,
  useDeleteContactMutation,
} from "../../store/api/contactApi";
import { ContactFormModal, ContactDetailModal } from "./components";
import { useTable } from "../../hooks/common/useTable";
import { useToast } from "../../hooks/common/useToast";
import { formatDate } from "../../utils/formatters";
import type {
  ContactResponseDto,
  ContactFilterDto,
} from "../../types/contact";

// Stats Card Component
const StatsCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, icon, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
    <div className="flex items-center">
      <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {title}
        </p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
          {value.toLocaleString()}
        </p>
      </div>
    </div>
  </div>
);

const ContactsPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactResponseDto | null>(null);

  const { showToast } = useToast();
  const {
    currentPage,
    pageSize,
    sortConfig,
    filters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleFilterChange,
    getQueryParams,
  } = useTable({
    key: "contacts",
    defaultPageSize: 25,
  });

  // Build query parameters
  const queryParams = {
    ...getQueryParams(),
    ...(sortConfig && {
      sortBy: sortConfig.key,
      sortOrder: sortConfig.direction?.toUpperCase() as "ASC" | "DESC",
    }),
  };

  // API Queries
  const {
    data: contactsData,
    isLoading,
    error,
    refetch,
  } = useGetContactsQuery(queryParams as ContactFilterDto);

  const [deleteContact] = useDeleteContactMutation();

  const contacts = contactsData?.data || [];
  const totalRecords = contactsData?.pagination?.total || 0;
  const stats = contactsData?.summary;

  const handleView = useCallback((contact: ContactResponseDto) => {
    setSelectedContact(contact);
    setIsDetailModalOpen(true);
  }, []);

  const handleEdit = useCallback((contact: ContactResponseDto) => {
    setSelectedContact(contact);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (contact: ContactResponseDto) => {
      try {
        await deleteContact(contact.id).unwrap();
        showToast("success", "Contact deleted successfully");
        refetch();
      } catch (error) {
        showToast("error", "Failed to delete contact");
      }
    },
    [deleteContact, showToast, refetch]
  );

  // Status badge renderer
  const renderStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: "bg-green-100 text-green-800", label: "Active" },
      inactive: { color: "bg-gray-100 text-gray-800", label: "Inactive" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: "bg-gray-100 text-gray-800",
      label: status,
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Table Columns
  const columns: DataTableColumn<ContactResponseDto>[] = [
    {
      key: "id",
      title: "ID",
      sortable: true,
      width: "80px",
      render: (value) => `#${value}`,
    },
    {
      key: "name",
      title: "Name",
      sortable: true,
      render: (value, record) => (
        <div className="flex items-center">
          <User className="h-4 w-4 text-gray-400 mr-2" />
          <span className="font-medium">{String(value)}</span>
        </div>
      ),
    },
    {
      key: "contact",
      title: "Contact Information",
      sortable: false,
      render: (value) => (
        <div className="flex items-center">
          <Mail className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-blue-600 hover:text-blue-800 max-w-xs truncate" title={String(value)}>
            {String(value)}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      title: "Status",
      sortable: true,
      render: (value) => renderStatusBadge(String(value)),
    },
    {
      key: "createdAt",
      title: "Created",
      sortable: true,
      render: (value) => (
        <div className="flex items-center text-sm text-gray-600">
          <Calendar className="h-4 w-4 mr-1" />
          {formatDate(value as Date)}
        </div>
      ),
    },
  ];

  // Table Actions
  const actions: DataTableAction<ContactResponseDto>[] = [
    {
      key: "view",
      label: "View",
      icon: <Eye className="h-4 w-4" />,
      onClick: handleView,
      variant: "ghost",
    },
    {
      key: "edit",
      label: "Edit",
      icon: <Edit className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "ghost",
    },
    {
      key: "delete",
      label: "Delete",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger",
    },
  ];

  // Bulk Actions
  const bulkActions: DataTableBulkAction<ContactResponseDto>[] = [
    {
      key: "delete",
      label: "Delete Selected",
      icon: <Trash2 className="h-4 w-4" />,
      variant: "danger",
      onClick: async (selectedItems) => {
        // TODO: Implement bulk delete
        console.log("Bulk delete:", selectedItems);
      },
    },
  ];

  // Modal Handlers
  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedContact(null);
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedContact(null);
  };

  const handleCreateSuccess = () => {
    refetch();
    handleCreateModalClose();
    showToast("success", "Contact created successfully");
  };

  const handleEditSuccess = () => {
    refetch();
    handleEditModalClose();
    showToast("success", "Contact updated successfully");
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error loading contacts
          </h3>
          <p className="text-gray-600 mb-4">
            There was an error loading the contacts data.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Contacts"
        action={{
          label: "Add Contact",
          icon: <Plus className="h-4 w-4" />,
          onClick: () => {
            // Check if maximum contacts limit (5) is reached
            if (totalRecords >= 5) {
              showToast("error", "Maximum of 5 contacts allowed. Please delete existing contacts to add new ones.");
              return;
            }
            setIsCreateModalOpen(true);
          },
        }}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatsCard
          title="Total Contacts"
          value={stats?.totalContacts || totalRecords}
          icon={<User className="h-5 w-5" />}
          color="bg-blue-100 text-blue-600"
        />
        <StatsCard
          title="Active"
          value={stats?.activeContacts || 0}
          icon={<Mail className="h-5 w-5" />}
          color="bg-green-100 text-green-600"
        />
        <StatsCard
          title="Inactive"
          value={stats?.inactiveContacts || 0}
          icon={<Eye className="h-5 w-5" />}
          color="bg-gray-100 text-gray-600"
        />
      </div>

      {/* Data Table */}
      {isLoading ? (
        <LoadingSpinner />
      ) : contacts.length === 0 ? (
        <EmptyState
          icon={<User className="h-12 w-12" />}
          title="No contacts found"
          description="Get started by creating your first contact."
          action={{
            label: "Add Contact",
            onClick: () => {
              // Check if maximum contacts limit (5) is reached
              if (totalRecords >= 5) {
                showToast("error", "Maximum of 5 contacts allowed. Please delete existing contacts to add new ones.");
                return;
              }
              setIsCreateModalOpen(true);
            },
          }}
        />
      ) : (
        <DataTable<ContactResponseDto>
          data={contacts}
          columns={columns}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize,
            total: totalRecords,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: true,
            onChange: handlePageChange,
          }}
          sortable
          onSort={(sort) => {
            if (sort) {
              handleSort(sort.field);
            }
          }}
          searchable
          searchPlaceholder="Search by name or contact information..."
          onSearch={(value) => handleFilterChange("search", value)}
          actions={actions}
        />
      )}

      {/* Modals */}
      <ContactFormModal
        isOpen={isCreateModalOpen}
        onClose={handleCreateModalClose}
        onSuccess={handleCreateSuccess}
        mode="create"
      />

      <ContactFormModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSuccess={handleEditSuccess}
        contact={selectedContact}
        mode="edit"
      />

      <ContactDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleDetailModalClose}
        onEdit={handleEdit}
        onDelete={handleDelete}
        contact={selectedContact}
      />
    </div>
  );
};

export default ContactsPage;